import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import Zone from "@insite/client-framework/Components/Zone";
import Logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryDepthLoaded,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@sunSource/Store/CustomApplicationState";
import TabsDataFilterHandler from "@sunSource/Store/Data/TabsData/Handler/TabsDataFilterHandler";
import TabsDataHandler from "@sunSource/Store/Data/TabsData/Handler/TabsDataHandler";
import isEqual from "lodash/isEqual";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    parentLink = "parentLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.parentLink]: LinkFieldValue;
    };
}

export interface MappedLink {
    id?: string;
    title: string;
    url: string;
    type?: string;
    excludeFromNavigation?: boolean;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    smallImagePath?: string;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
    isBackground?: boolean;
    customMenuTitle?: string;
    isCustomMenu?: boolean;
}

const mapStateToProps = (state: ApplicationState & CustomApplicationState, ownProps: OwnProps) => {
    const {
        fields: { parentLink },
    } = ownProps;
    const categoryIdsToLoad: Dictionary<number> = {};
    const pages = getDataView(state.data.pages, { parentNodeId: getCurrentPage(state).nodeId });
    const { type, value } = parentLink;

    let mappedLink: MappedLink | undefined;

    if (type === "Url") {
        mappedLink = {
            url: value,
            title: "",
        };
    } else if (type === "Page") {
        const readonlyPageLink = getPageLinkByNodeId(state, value);
        if (readonlyPageLink) {
            mappedLink = { ...readonlyPageLink };
        }
    } else if (type === "Category") {
        mappedLink = setupCategoryLink(state, value, categoryIdsToLoad, 1, "", state.data.categories.categoryIdsCalled);
    }

    return {
        link: mappedLink,
        location: getLocation(state),
        categoryIdsToLoad,
        categoryErrorStatusCodeById: state.data.categories.errorStatusCodeById,
        categoryIdsCalled: state.data.categories.categoryIdsCalled,
        categoryId: value,
        page: getCurrentPage(state),
        pages: pages.value || [],
        activeTab: state.sunSourceState.TabsActiveData.activeTab,
    };
};

const mapDispatchToProps = {
    loadCategories,
    TabsDataHandler,
    TabsDataFilterHandler
};

const getParameter = (categoryId: string, maxDepth: number) => ({
    maxDepth,
    startCategoryId: categoryId === emptyGuid ? undefined : categoryId,
    includeStartCategory: categoryId !== emptyGuid,
    expand: ["properties"],
});

const setupCategoryLink = (
    state: ApplicationState,
    value: string,
    categoryIdsToLoad: Dictionary<number>,
    maxDepth: number,
    overrideTitle: string,
    categoryIdsCalled: string[],
) => {
    let mappedLink: MappedLink | undefined;
    if (!value) {
        return;
    }
    const depthLoaded = getCategoryDepthLoaded(state, value);
    const subCategoryIds = getSubCategoryIds(state, value);

    if (
        (depthLoaded < maxDepth || (!subCategoryIds && !categoryIdsCalled?.includes(value))) &&
        !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
    ) {
        categoryIdsToLoad[value] = maxDepth;
    } else {
        if (value === emptyGuid) {
            mappedLink = {
                url: "",
                title: overrideTitle || translate("Products"),
            };
        } else {
            const categoryState = getCategoryState(state, value);
            if (!categoryState.value) {
                if (
                    !categoryState.isLoading &&
                    !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
                ) {
                    categoryIdsToLoad[value] = maxDepth;
                }
                return;
            }
            mappedLink = {
                url: categoryState.value.path,
                title: overrideTitle || categoryState.value.shortDescription,
            };
        }

        loadSubCategories(mappedLink, subCategoryIds, 1, maxDepth, state);
    }
    return mappedLink;
};

const loadSubCategories = (
    mappedLink: MappedLink,
    subCategoryIds: readonly string[] | undefined,
    currentDepth: number,
    maxDepth: number,
    state: ApplicationState,
    visited = new Set<string>()
) => {
    if (currentDepth > maxDepth) {
        return;
    }
    mappedLink.children = [];
    if (!subCategoryIds) {
        return;
    }
    for (const subCategoryId of subCategoryIds) {

        if (visited.has(subCategoryId)) {
            continue;
        }

        visited.add(subCategoryId);

        const subCategory = getCategoryState(state, subCategoryId).value;
        if (!subCategory) {
            Logger.error(`Category ${subCategoryId} was not found in data state, excluding from navigation.`);
            continue;
        }
        const childLink: MappedLink = {
            url: subCategory.path,
            title: subCategory.shortDescription,
            children: [],
        };
        mappedLink.children.push(childLink);

        loadSubCategories(childLink, subCategory.subCategoryIds, currentDepth + 1, maxDepth, state, visited );
    }
};

export interface TabStyles {
    wrapper?: InjectableCss;
    listWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    listTitle?: TypographyPresentationProps;
    list?: InjectableCss;
    listItem?: InjectableCss;
}

export const tabStyles: TabStyles = {
    wrapper: {
        css: css`
            background: #e7e6ec;
            padding: 20px 15px 0px;
            position: relative;
            width: 100%;
        `,
    },
    listWrapper: {
        css: css`
            span {
                &:hover {
                    color: ${getColor("primary")} !important;
                }
            }
        `,
    },
    listTitle: {
        weight: 600,
        css: css`
            color: ${getColor("secondary")};
            text-transform: capitalize;
            margin-bottom: 15px;
            font-size: 18px;
            position: relative;
            > .titleActiveBar {
                background: ${getColor("primary")};
                width: 28px;
                height: 3px;
                display: inline-block;
                transform: rotateZ(-48deg);
                margin-bottom: 6px;
                margin-left: 2px;
            }
        `,
    },
    link: {
        typographyProps: {
            variant: "headerTertiary",
        },
    },
    list: {
        css: css`
            display: flex;
            flex-direction: column;
            list-style: none;
            padding-left: 0;
            gap: 20px;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: flex-start;
        `,
    },
    listItem: {
        css: css`
            background: #d9d9d9;
            padding: 15px 20px;
            width: fit-content;
            border-radius: 4px;
            border-bottom-right-radius: 0px;
            border-bottom-left-radius: 0px;
            font-weight: 600 !important;
            font-size: 16px !important;
            color: #756e6e !important;
            text-transform: uppercase;
            cursor: pointer;
            min-width: 170px;
            text-align: center;
            &.active {
                background: #fff;
                color: #00539b !important;
            }
        `,
    },
};

const styles = tabStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export class Tab extends React.Component<Props> {
    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (!isEqual(this.props.categoryIdsToLoad, prevProps.categoryIdsToLoad)) {
            this.loadCategoriesIfNeeded(true);
        }

        if (prevProps.location.search !== this.props.location.search) {
            this.syncActiveTabFromUrl();
        }
    }

    componentDidMount() {
        this.loadCategoriesIfNeeded();
        this.syncActiveTabFromUrl();
    }

    syncActiveTabFromUrl = () => {
        if (typeof window === "undefined" || !window.location) {
            return;
        }

        const url = new URL(window.location.href);
        const tabValue = url.searchParams.get("tab") ?? "";

        if (tabValue !== this.props.activeTab) {
            this.props.TabsDataHandler({ activeTab: tabValue });
        }
    };

    loadCategoriesIfNeeded(isComponentDidUpdate = false) {
        const { categoryIdsToLoad, categoryIdsCalled, categoryId, categoryErrorStatusCodeById, loadCategories } =
            this.props;
        if (!categoryErrorStatusCodeById?.[categoryId]) {
            const maxDepth = categoryIdsToLoad[categoryId];
            if (!categoryIdsCalled?.includes(categoryId)) {
                loadCategories(getParameter(categoryId, maxDepth));
            }
        }
    }

    tabOnChange = (tab: string) => {
        this.props.TabsDataHandler({ activeTab: tab });
        // Add or update tab parameter in URL, preserving other params
        
        if (typeof window !== "undefined" && window.history && window.location) {
            const url = new URL(window.location.href);
            // Always remove 'search' and 'tag' params when switching tabs
            url.searchParams.delete("search");
            url.searchParams.delete("tag");
            url.searchParams.delete("page"); // Reset to first page when switching tabs
            if (tab !== this.props.activeTab && tab !== "") {
                url.searchParams.set("tab", tab);
            } else if (tab === "") {
                url.searchParams.delete("tab");
            }
            this.props.history.replace(url.pathname + url.search);
        }
    };

    render() {
        const { link, pages, activeTab } = this.props;

        if (!link) {
            return null;
        }

        const categoriesName = pages.length > 0 && [
            ...new Set(pages?.map(item => item.generalFields["categoryListName"])),
        ];

        return (
            <StyledWrapper {...styles.wrapper} data-test-selector="tab">
                <StyledWrapper className="list-wrapper" {...styles.listWrapper}>
                    <StyledWrapper {...styles.list} className="subChildren" as="ul">
                        <StyledWrapper
                            {...styles.listItem}
                            as="li"
                            className={
                                link?.title?.trim()?.toLowerCase() === activeTab?.trim()?.toLowerCase() || activeTab === ""
                                    ? "active"
                                    : ""
                            }
                            onClick={() => this.tabOnChange("")}
                        >
                            {"All Content"}
                        </StyledWrapper>
                        {categoriesName &&
                            categoriesName.map((item, index) => {
                                return (
                                    <StyledWrapper
                                        {...styles.listItem}
                                        as="li"
                                        key={`${item}_${index}`}
                                        className={
                                            item?.trim()?.toLowerCase() === activeTab?.trim()?.toLowerCase() ? "active" : ""
                                        }
                                        onClick={() => this.tabOnChange(item)}
                                    >
                                        {item}
                                    </StyledWrapper>
                                );
                            })}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper data-test-selector="content-block">
                    <Zone contentId={this.props.id} zoneName="ContentBlock" />
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(Tab)),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: "parentLink",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                allowUrls: () => false,
                isRequired: true,
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
